import { FileText, Target, TrendingUp, Layers, BarChart3, Crosshair } from "lucide-react";
import { SectionHeader } from "./SectionHeader";

interface MetadataHeaderProps {
  studyName: string;
  nTrials: number;
  symbol: string;
  timeframe: string;
  parallelCount: number;
  minimumTrades: number;
  objectiveFunction: "sortino" | "sharpe";
}

export function MetadataHeader({
  studyName,
  nTrials,
  symbol,
  timeframe,
  parallelCount,
  minimumTrades,
  objectiveFunction,
}: MetadataHeaderProps) {
  return (
    <div className="space-y-3">
      <SectionHeader title="Study Metadata" gradient="blue-cyan" />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {/* Study Name - spans full width on mobile */}
        <div className="col-span-2 md:col-span-1 flex items-start gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/5">
          <FileText className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Study Name</p>
            <p className="text-sm font-medium truncate" title={studyName}>
              {studyName}
            </p>
          </div>
        </div>

        {/* Symbol & Timeframe */}
        <div className="flex items-start gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/5">
          <TrendingUp className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Symbol & Timeframe</p>
            <p className="text-sm font-medium">
              {symbol} / {timeframe}
            </p>
          </div>
        </div>

        {/* Objective Function */}
        <div className="flex items-start gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/5">
          <Crosshair className="h-5 w-5 text-rose-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Objective</p>
            <p className="text-sm font-medium capitalize">
              {objectiveFunction} Ratio
            </p>
          </div>
        </div>

        {/* Trials */}
        <div className="flex items-start gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/5">
          <Target className="h-5 w-5 text-purple-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Trials</p>
            <p className="text-sm font-medium">{nTrials.toLocaleString()}</p>
          </div>
        </div>

        {/* Parallel Versions */}
        <div className="flex items-start gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/5">
          <Layers className="h-5 w-5 text-cyan-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Parallel Versions</p>
            <p className="text-sm font-medium">
              {parallelCount === 1 ? "1 (Single)" : `${parallelCount} Parallel`}
            </p>
          </div>
        </div>

        {/* Minimum Trades */}
        <div className="flex items-start gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/5">
          <BarChart3 className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Min. Trades Required</p>
            <p className="text-sm font-medium">{minimumTrades}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
